import { useState, useEffect, useRef, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, FolderPlus, Upload, Download, Trash2, FileText, FolderOpen, Home, ChevronRight, PenTool, CheckCircle, X, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const MyArchivePage = () => {
  const { user, authAxios } = useAuth();
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadProgress, setUploadProgress] = useState(false);
  const [showSignModal, setShowSignModal] = useState(null);
  const [signerName, setSignerName] = useState("");
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [foldersRes, docsRes, statsRes] = await Promise.all([
        authAxios.get("/documents/folders"),
        authAxios.get(`/documents${currentFolder ? `?folder_id=${currentFolder}` : ""}`),
        authAxios.get("/documents/stats/summary")
      ]);
      setFolders(foldersRes.data); setDocuments(docsRes.data); setStats(statsRes.data);
    } catch (error) { /* error handled */; } finally { setLoading(false); }
  }, [authAxios, currentFolder]);

  useEffect(() => { if (user) fetchData(); }, [currentFolder, user, fetchData]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) { fetchData(); return; }
    try { const r = await authAxios.get(`/documents?search=${searchQuery}`); setDocuments(r.data); } catch (e) { /* error handled */; }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try { await authAxios.post("/documents/folders", { name: newFolderName, parent_id: currentFolder }); toast.success("Carpeta creada"); setNewFolderName(""); setShowNewFolder(false); fetchData(); }
    catch (error) { toast.error("Error al crear carpeta"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    if (currentFolder) fd.append("folder_id", currentFolder);
    setUploadProgress(true);
    try { await authAxios.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Documento subido"); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || "Error al subir"); }
    finally { setUploadProgress(false); }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await authAxios.delete(`/documents/${docId}`); toast.success("Eliminado"); fetchData(); } catch (e) { toast.error("Error"); }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm("¿Eliminar carpeta?")) return;
    try { await authAxios.delete(`/documents/folders/${folderId}`); toast.success("Carpeta eliminada"); fetchData(); } catch (e) { toast.error("Error"); }
  };

  const handleDownload = async (doc) => {
    try { const r = await authAxios.get(`/documents/${doc.id}`); const link = document.createElement("a"); link.href = `data:${doc.file_type};base64,${r.data.file_data}`; link.download = doc.name; link.click(); }
    catch (e) { toast.error("Error al descargar"); }
  };

  // Drag & Drop
  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragOver(true); }, []);
  const handleDragLeave = useCallback(() => setIsDragOver(false), []);
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    const file = files[0];
    const fd = new FormData();
    fd.append("file", file);
    if (currentFolder) fd.append("folder_id", currentFolder);
    setUploadProgress(true);
    try {
      await authAxios.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} subido exitosamente`);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al subir"); }
    finally { setUploadProgress(false); }
  }, [currentFolder, authAxios, fetchData]);

  // PDF / Image Preview
  const handlePreview = async (doc) => {
    try {
      const r = await authAxios.get(`/documents/${doc.id}`);
      setPreviewDoc(doc);
      setPreviewData(r.data.file_data);
    } catch (e) { toast.error("Error al previsualizar"); }
  };

  // Signature functions
  const initCanvas = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#5D4037";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => initCanvas();

  const handleSign = async () => {
    if (!signerName.trim()) { toast.error("Ingresa el nombre del firmante"); return; }
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL("image/png").split(",")[1];
    try {
      await authAxios.post(`/documents/${showSignModal.id}/sign`, {
        signature_data: signatureData,
        signer_name: signerName
      });
      toast.success("Documento firmado exitosamente");
      setShowSignModal(null);
      setSignerName("");
      fetchData();
    } catch (error) { toast.error("Error al firmar documento"); }
  };

  if (!user) return <Navigate to="/login" />;
  const currentFolderData = folders.find(f => f.id === currentFolder);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="my-archive-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div><h1 className="text-3xl font-bold text-[#5D4037]">Mi Archivo Digital</h1><p className="text-stone-600">Bienvenido, {user.name}</p></div>
          {stats && (
            <div className="flex gap-4">
              <div className="bg-white rounded-xl px-4 py-2 border border-stone-200"><p className="text-xs text-stone-500">Documentos</p><p className="text-xl font-bold text-[#2E7D32]">{stats.total_documents}</p></div>
              <div className="bg-white rounded-xl px-4 py-2 border border-stone-200"><p className="text-xs text-stone-500">Almacenamiento</p><p className="text-xl font-bold text-[#2E7D32]">{stats.storage_used_mb} MB</p></div>
            </div>
          )}
        </div>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} placeholder="Buscar documentos..." className="w-full h-12 pl-12 pr-4 rounded-xl border border-stone-200 focus:border-[#2E7D32] outline-none" data-testid="archive-search-input" />
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowNewFolder(true)} className="btn-secondary h-12 flex items-center gap-2" data-testid="new-folder-btn"><FolderPlus size={18} /> Nueva Carpeta</button>
            <label className="btn-primary h-12 flex items-center gap-2 cursor-pointer"><Upload size={18} /> {uploadProgress ? "Subiendo..." : "Subir"}<input type="file" className="hidden" onChange={handleUpload} disabled={uploadProgress} data-testid="upload-input" /></label>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-6 text-sm">
          <button onClick={() => setCurrentFolder(null)} className={`flex items-center gap-1 ${!currentFolder ? "text-[#2E7D32] font-semibold" : "text-stone-500 hover:text-stone-700"}`}><Home size={16} /> Inicio</button>
          {currentFolderData && (<><ChevronRight size={16} className="text-stone-400" /><span className="text-[#2E7D32] font-semibold">{currentFolderData.name}</span></>)}
        </div>
        {showNewFolder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-bold text-[#5D4037] mb-4">Nueva Carpeta</h3>
              <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Nombre" className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none mb-4" data-testid="folder-name-input" />
              <div className="flex gap-3"><button onClick={() => setShowNewFolder(false)} className="flex-1 h-12 border border-stone-200 rounded-xl font-medium">Cancelar</button><button onClick={handleCreateFolder} className="flex-1 btn-primary h-12" data-testid="create-folder-btn">Crear</button></div>
            </div>
          </div>
        )}
        {loading ? <div className="text-center py-12">Cargando...</div> : (
          <div className="space-y-6" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            {/* Drag & Drop Zone */}
            {isDragOver && (
              <div className="fixed inset-0 bg-[#2E7D32]/20 z-40 flex items-center justify-center pointer-events-none">
                <div className="bg-white rounded-3xl p-12 shadow-2xl border-4 border-dashed border-[#2E7D32] text-center">
                  <Upload size={48} className="text-[#2E7D32] mx-auto mb-4" />
                  <p className="text-xl font-bold text-[#5D4037]">Suelta tu archivo aquí</p>
                  <p className="text-stone-500">Se subirá automáticamente</p>
                </div>
              </div>
            )}
            {!currentFolder && folders.filter(f => !f.parent_id).length > 0 && (
              <div><h3 className="text-sm font-semibold text-stone-500 mb-3">CARPETAS</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {folders.filter(f => !f.parent_id).map((folder) => (
                    <div key={folder.id} className="bento-card p-4 cursor-pointer group hover:border-[#2E7D32]" data-testid={`folder-${folder.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${folder.color}20` }} onClick={() => setCurrentFolder(folder.id)}>
                          <FolderOpen size={20} style={{ color: folder.color }} />
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                      </div>
                      <p className="font-medium text-[#5D4037] truncate" onClick={() => setCurrentFolder(folder.id)}>{folder.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div><h3 className="text-sm font-semibold text-stone-500 mb-3">DOCUMENTOS</h3>
              {documents.length === 0 ? (
                <div className="bento-card p-12 text-center"><FileText size={48} className="text-stone-300 mx-auto mb-4" /><p className="text-stone-500">No hay documentos</p></div>
              ) : (
                <div className="grid gap-4">{documents.map((doc) => (
                  <div key={doc.id} className="bento-card p-4 flex items-center gap-4" data-testid={`document-${doc.id}`}>
                    <div className="w-12 h-12 bg-[#F57C00]/10 rounded-xl flex items-center justify-center flex-shrink-0"><FileText size={24} className="text-[#F57C00]" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-[#5D4037] truncate">{doc.name}</p>
                        {doc.signed && <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full"><CheckCircle size={12} /> Firmado</span>}
                      </div>
                      <p className="text-sm text-stone-500">{(doc.file_size / 1024).toFixed(1)} KB{doc.signed_by ? ` - Firmado por ${doc.signed_by}` : ""}</p>
                    </div>
                    <div className="flex gap-2">
                      {(doc.file_type?.includes("pdf") || doc.file_type?.includes("image")) && (
                        <button onClick={() => handlePreview(doc)} className="p-2 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Previsualizar" data-testid={`preview-doc-${doc.id}`}><Eye size={18} /></button>
                      )}
                      {!doc.signed && (
                        <button onClick={() => { setShowSignModal(doc); setSignerName(user.name); setTimeout(initCanvas, 100); }} className="p-2 text-stone-500 hover:text-[#2E7D32] hover:bg-[#2E7D32]/10 rounded-lg" title="Firmar" data-testid={`sign-doc-${doc.id}`}><PenTool size={18} /></button>
                      )}
                      <button onClick={() => handleDownload(doc)} className="p-2 text-stone-500 hover:text-[#2E7D32] hover:bg-stone-100 rounded-lg"><Download size={18} /></button>
                      <button onClick={() => handleDeleteDocument(doc.id)} className="p-2 text-stone-500 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
                    </div>
                  </div>
                ))}</div>
              )}
            </div>
          </div>
        )}
        {/* Preview Modal */}
        {previewDoc && previewData && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => { setPreviewDoc(null); setPreviewData(null); }}>
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-stone-200">
                <h3 className="font-bold text-[#5D4037] truncate">{previewDoc.name}</h3>
                <button onClick={() => { setPreviewDoc(null); setPreviewData(null); }} className="p-1 hover:bg-stone-100 rounded"><X size={20} /></button>
              </div>
              <div className="overflow-auto" style={{ maxHeight: "calc(85vh - 60px)" }}>
                {previewDoc.file_type?.includes("pdf") ? (
                  <iframe src={`data:application/pdf;base64,${previewData}`} className="w-full" style={{ height: "75vh" }} title={previewDoc.name} data-testid="pdf-preview" />
                ) : previewDoc.file_type?.includes("image") ? (
                  <img src={`data:${previewDoc.file_type};base64,${previewData}`} alt={previewDoc.name} className="max-w-full mx-auto" data-testid="image-preview" />
                ) : (
                  <p className="text-center py-12 text-stone-500">Previsualización no disponible para este tipo de archivo</p>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Signature Modal */}
        {showSignModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#5D4037]">Firmar Documento</h3>
                <button onClick={() => setShowSignModal(null)} className="p-1 hover:bg-stone-100 rounded"><X size={20} /></button>
              </div>
              <p className="text-sm text-stone-600 mb-4">Firmando: <strong>{showSignModal.name}</strong></p>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-[#5D4037] mb-2">Nombre del firmante</label>
                <input type="text" value={signerName} onChange={(e) => setSignerName(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" data-testid="signer-name-input" />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-[#5D4037] mb-2">Firma</label>
                <div className="border-2 border-dashed border-stone-300 rounded-xl overflow-hidden">
                  <canvas ref={canvasRef} width={440} height={150} className="w-full cursor-crosshair" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} data-testid="signature-canvas" />
                </div>
                <button onClick={clearSignature} className="text-sm text-stone-500 hover:text-stone-700 mt-2">Limpiar firma</button>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowSignModal(null)} className="flex-1 h-12 border border-stone-200 rounded-xl font-medium">Cancelar</button>
                <button onClick={handleSign} className="flex-1 btn-primary h-12 flex items-center justify-center gap-2" data-testid="confirm-sign-btn"><PenTool size={16} /> Firmar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyArchivePage;
